import React, { useState, useEffect } from "react";
import {
  Box, Flex, Input, Button, IconButton, VStack, Text, Spinner,
  Heading, Drawer, DrawerBody, DrawerContent, DrawerOverlay,
  DrawerHeader, Divider, Alert, AlertIcon, AlertTitle,
  AlertDescription, CloseButton, Link, HStack, Badge, useDisclosure, useToast,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";

import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import usePriceData from "../hooks/usePriceData";
import useWatchlist from "../hooks/useWatchlist";
import useSearchTickers from "../hooks/useSearchTickers";
import WatchlistSidebar from "../components/WatchlistSidebar";
import apiClient from "../api/apiClient";
import GaugeChart from "react-gauge-chart";

const NewsAnalyzer = () => {
  const { user, logout, updateAiUsage } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [llmResult, setLlmResult] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [loadingLlm, setLoadingLlm] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // === Custom Hooks ===
  const { watchlist, loading, addTicker, removeTicker } = useWatchlist(navigate);
  const { priceData, lastUpdated } = usePriceData(watchlist);
  const searchSuggestions = useSearchTickers(searchInput);

  // === Ask LLM ===
  const askLLM = async (ticker) => {
    if (!ticker) return;
    if (user.aiQueriesUsed >= user.limits.aiQueries && !user.isTestUser) {
      setErrorMessage("AI query limit reached. Please upgrade your plan.");
      return;
    }

    setSelectedTicker(ticker);
    setLlmResult(null);
    setLoadingLlm(true);
    if (window.innerWidth < 768) onClose();

    try {
      const res = await apiClient.post("/ask", { ticker });
      setLlmResult(res.data.answer);
      updateAiUsage(res.data.usage.aiUsed ?? user.aiQueriesUsed, res.data.usage.aiLimit ?? user.limits.aiQueries);
      document.getElementById("llm-result")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      console.error("LLM error:", err);
      if (err.response?.status === 401) logout();
      setErrorMessage(err.response?.data?.error || "Error fetching LLM result");
    } finally {
      setLoadingLlm(false);
    }
  };

  // Listen for global watchlist selection events (floating button / layout drawer)
  useEffect(() => {
    const handler = (e) => {
      const ticker = e.detail;
      if (ticker) askLLM(ticker);
    };
    window.addEventListener('watchlist-select', handler);
    return () => window.removeEventListener('watchlist-select', handler);
  }, [askLLM]);

  // === Search input handling ===
  const handleSearchInput = (e) => setSearchInput(e.target.value.toUpperCase());
  const handleSearchSelect = (ticker) => {
    setSearchInput("");
    askLLM(ticker);
  };

  // === Helpers ===
  const formatLocalTime = (utcString) => {
    if (!utcString) return "";
    const date = new Date(utcString);
    return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  };

  const sentimentColor = (sentiment) => {
    switch (sentiment) {
      case "Very Positive": return "green";
      case "Positive": return "teal";
      case "Neutral": return "gray";
      case "Negative": return "orange";
      case "Very Negative": return "red";
      default: return "gray";
    }
  };



  // === Sentiment Distribution Bar ===
  const SentimentDistributionBar = ({ distribution }) => {
    const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;
    const colors = {
      veryPositive: "green.500",
      positive: "green.300",
      neutral: "gray.400",
      negative: "orange.400",
      veryNegative: "red.500",
    };
    const formatLabel = (key) =>
      key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();

    return (
      <Box mb={4}>
        <Text fontWeight="bold" mb={2}>News Sentiment Distribution</Text>
        <Flex h="20px" borderRadius="md" overflow="hidden">
          {Object.entries(distribution).map(([key, value]) => {
            const percentage = (value / total) * 100;
            return <Box key={key} w={`${percentage}%`} bg={colors[key]} title={`${formatLabel(key)}: ${percentage.toFixed(1)}%`} />;
          })}
        </Flex>
        <Flex justify="space-between" mt={2} fontSize="xs">
          {Object.entries(distribution).map(([key, value]) => {
            const percentage = (value / total) * 100;
            return <Text key={key} color={colors[key]}>{formatLabel(key)}: {percentage.toFixed(1)}%</Text>;
          })}
        </Flex>
      </Box>
    );
  };

  // === Render ===
  return (
    <Flex direction={{ base: "column", md: "row" }} h="100vh">


      {/* Sidebar (mobile drawer + desktop sidebar) */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Watchlist</DrawerHeader>
          <DrawerBody>
            <WatchlistSidebar
              watchlist={watchlist}
              priceData={priceData}
              selectedTicker={selectedTicker}
              onSelectTicker={askLLM}
              onRemoveTicker={removeTicker}
              loading={loading}
              lastUpdated={lastUpdated}
              onClose={onClose}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box flex="0 0 230px" display={{ base: "none", md: "block" }}>
        <WatchlistSidebar
          watchlist={watchlist}
          priceData={priceData}
          selectedTicker={selectedTicker}
          onSelectTicker={askLLM}
          onRemoveTicker={removeTicker}
          loading={loading}
          lastUpdated={lastUpdated}
        />
      </Box>

      {/* Main Area */}
      <Flex flex="1" direction="column">
        <Box flex="1" p={6} overflowY="auto">
          {errorMessage && (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
              <CloseButton position="absolute" right="8px" top="8px" onClick={() => setErrorMessage(null)} />
            </Alert>
          )}

          {/* Search */}
          <Box position="relative" mb={3}>
            <Input placeholder="Search ticker or company..." value={searchInput} onChange={handleSearchInput} />
            {searchSuggestions.length > 0 && (
              <Box position="absolute" top="100%" left="0" right="0" bg="white" shadow="md" borderRadius="md" zIndex={10}>
                <VStack spacing={0}>
                  {searchSuggestions.map((item) => (
                    <Flex key={item.ticker} justify="space-between" align="center" w="full" px={3} py={2} _hover={{ bg: "gray.50" }}>
                      <Button variant="link" colorScheme="teal" onClick={() => handleSearchSelect(item.ticker)}>
                        {item.ticker} - {item.name}
                      </Button>
                      <IconButton
                        icon={<AddIcon />}
                        size="xs"
                        colorScheme="green"
                        aria-label="Add"
                        onClick={() => {
                          addTicker(item.ticker);
                          setSearchInput("");
                        }}
                      />
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </Box>

          <Divider my={4} />

          {/* LLM Result */}
          {loadingLlm ? (
            <HStack>
              <Spinner />
              <Text>AI is analyzing {selectedTicker}...</Text>
            </HStack>
          ) : (
            <Box id="llm-result" mt={4} p={4} bg="white" borderRadius="md" shadow="sm">
              {selectedTicker && llmResult ? (
                <>
                  <Text fontWeight="bold" mb={2}>
                    🧠 Today's Mood for <strong>{selectedTicker}</strong>:
                  </Text>

                  {/* === Half-circle sentiment gauge === */}
                  {llmResult.news?.length > 0 && <Box w="180px" mx="auto" mb={2}>
                    <GaugeChart
                      id="ticker-sentiment-gauge"
                      nrOfLevels={20}
                      arcsLength={[0.33, 0.33, 0.34]}
                      colors={["#E57373", "#FFD54F", "#81C784"]} // red → yellow → green
                      percent={(llmResult.sentiment_score || 50) / 100}
                      arcPadding={0.02}
                      cornerRadius={3}
                      arcWidth={0.3}
                      needleColor="#555"
                      textColor="#333"
                      hideText={true}
                      style={{ width: "100%" }}
                    />
                  </Box>
              }

                  {/* Optional sentiment label */}
                  {llmResult.news?.length > 0 && llmResult.overall_sentiment && llmResult.sentiment_score > 0 && (
                    <Text textAlign="center" fontWeight="semibold" fontSize="sm" mb={2}>
                      {llmResult.sentiment_score}% {llmResult.overall_sentiment.toUpperCase()}
                    </Text>
                  )}


                  <Text mb={4}>
                    {llmResult.summary}
                  </Text>

                  {/* Takeaways */} {llmResult.takeaways?.length > 0 && (<Box mb={4}>  <VStack align="start" spacing={2}> {llmResult.takeaways.map((t, idx) => (<Box key={idx}> <Text fontSize="sm" fontWeight="medium"> • {t.title || t} </Text> {t.description && (<Text fontSize="sm" color="gray.500" ml={4}> {t.description} </Text>)} </Box>))} </VStack> </Box>)}

                  {llmResult.news?.length > 0 && (
                    <SentimentDistributionBar
                      distribution={llmResult.news.reduce(
                        (acc, item) => {
                          const s = (item.sentiment || "Neutral").toLowerCase();
                          if (s.includes("very positive")) acc.veryPositive++;
                          else if (s.includes("positive")) acc.positive++;
                          else if (s.includes("very negative")) acc.veryNegative++;
                          else if (s.includes("negative")) acc.negative++;
                          else acc.neutral++;
                          return acc;
                        },
                        { veryPositive: 0, positive: 0, neutral: 0, negative: 0, veryNegative: 0 }
                      )}
                    />
                  )}
                  {llmResult.news?.length > 0 && <Text fontWeight="bold" mb={2}>Impactful News:</Text>}
                  {llmResult.news.map((item, idx) => (
                    <Box key={idx} p={2} bg="gray.50" borderRadius="md" mb={2}>
                      <Link href={item.url} color="blue.500" isExternal fontSize="sm" fontWeight="medium">
                        {item.headline}
                      </Link>
                      <HStack spacing={2} mt={1}>
                        <Badge colorScheme={sentimentColor(item.sentiment)}>{item.sentiment}</Badge>
                        <Text fontSize="xs" color="gray.500">
                          {item.source} | {formatLocalTime(item.publishedDate)}
                        </Text>
                      </HStack>
                      {item.impact && <Text fontSize="sm" mt={1}>{item.impact}</Text>}
                    </Box>
                  ))}


                </>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Select a ticker from your watchlist or search above to see today's mood.
                </Text>
              )}
            </Box>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

export default NewsAnalyzer;
